import { fetcher } from "@certn/api";
import { createAjv, isEnabled } from "@jsonforms/core";

import { ApplicationSchema, isApplicationSchema } from "../../types";
import { getCaseDetailEndpoint, getNextSection, getSection } from "../../utils";
import { KonfirVerificationStatus } from "./types";

export const TERMINAL_KONFIR_STATUSES: KonfirVerificationStatus[] = [
  "COMPLETED",
  "FAILED",
  "EXPIRED",
  "BYPASSED",
];

export const isTerminalKonfirStatus = (
  status: KonfirVerificationStatus | null
): boolean => !!status && TERMINAL_KONFIR_STATUSES.includes(status);

export const deriveStateFromKonfirStatus = (
  status: KonfirVerificationStatus | null
): {
  isComplete: boolean;
  isRecoverable: boolean;
  isInProgress: boolean;
  isInitial: boolean;
  isSkipped: boolean;
} => {
  const isComplete = status === "COMPLETED";
  const isRecoverable = status ? ["FAILED", "EXPIRED"].includes(status) : false;
  const isInProgress = status === "IN_PROGRESS";
  const isInitial = status === null;
  const isSkipped = status === "BYPASSED";

  return { isComplete, isRecoverable, isInProgress, isInitial, isSkipped };
};

export const openKonfirWindow = (url: string) => {
  const width = window.innerWidth * 0.8;
  const height = window.innerHeight * 0.8;
  const left = (window.innerWidth - width) / 2;
  const top = window.innerHeight - height;
  window.open(
    url,
    "_blank",
    `width=${width},height=${height},left=${left},top=${top}`
  );
};

export const getKonfirError = (error: unknown, fallback: string): string => {
  const apiError = error as {
    body?: { failure_reason?: { cause_type?: string; error_message?: string } };
  };
  const reason = apiError?.body?.failure_reason;
  if (reason) {
    return `${reason.cause_type ?? "Error"}: ${reason.error_message ?? "Unknown error"}`;
  }
  return fallback;
};

type CaseDetailResponse = {
  ui_schema: ApplicationSchema;
  applicant_form_data: unknown;
};

export async function resolvePostKonfirSkipDestination(
  caseId: string,
  basePath: string | undefined
): Promise<string> {
  const endpoint = getCaseDetailEndpoint(basePath, caseId);
  const { ui_schema, applicant_form_data } =
    await fetcher.request<CaseDetailResponse>(endpoint);

  if (!isApplicationSchema(ui_schema)) {
    return "summary";
  }

  const documentsSection = getSection("employment-documents", ui_schema);
  if (
    documentsSection &&
    isEnabled(documentsSection, applicant_form_data, "", createAjv())
  ) {
    return "employment-documents";
  }

  const nextSection = getNextSection(
    "employment-verification",
    ui_schema,
    applicant_form_data
  );
  return nextSection?.name ?? "summary";
}
